import { useState } from "react";
import { Gavel, Plus, CheckCircle2, XCircle, ExternalLink, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { useOrgFines, useMyFines, useIssueFine, useDisputeFine, useResolveFine } from "@/hooks/useFines";
import { useHosts } from "@/hooks/useHosts";
import type { Fine, FineStatus } from "@/types/database";

const STATUS_COLORS: Record<FineStatus, string> = {
  issued: "bg-warning/10 text-warning border-warning/20",
  disputed: "bg-orange-100 text-orange-800 border-orange-200",
  upheld: "bg-destructive/10 text-destructive border-destructive/20",
  waived: "bg-muted text-muted-foreground border-border",
  paid: "bg-success/10 text-success border-success/20",
  overdue: "bg-destructive/10 text-destructive border-destructive/20",
};

const ADMIN_ROLES = new Set(["strata_admin", "property_manager", "system_admin"]);

export default function Fines() {
  const { user } = useAuth();
  const isAdmin = !!user && ADMIN_ROLES.has(user.role);

  const orgFines = useOrgFines();
  const myFines = useMyFines();
  const { data: fines = [], isLoading, error } = isAdmin ? orgFines : myFines;
  const { data: hosts = [] } = useHosts();

  const issueFine = useIssueFine();
  const disputeFine = useDisputeFine();
  const resolveFine = useResolveFine();

  // Issue dialog (admin)
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueForm, setIssueForm] = useState({ host_id: "", reason: "", amount: "", due_date: "" });

  // Detail / action dialog
  const [activeFine, setActiveFine] = useState<Fine | null>(null);
  const [disputeReason, setDisputeReason] = useState("");
  const [resolutionNote, setResolutionNote] = useState("");

  const resetIssueForm = () => setIssueForm({ host_id: "", reason: "", amount: "", due_date: "" });

  const handleIssue = () => {
    const host = hosts.find((h: any) => h.id === issueForm.host_id);
    if (!issueForm.host_id || !issueForm.reason.trim() || !issueForm.amount || !issueForm.due_date) return;
    issueFine.mutate(
      {
        host_id: issueForm.host_id,
        unit_id: host?.unit_id,
        reason: issueForm.reason.trim(),
        amount: parseFloat(issueForm.amount),
        due_date: issueForm.due_date,
      },
      { onSuccess: () => { setIssueOpen(false); resetIssueForm(); } }
    );
  };

  const openFine = (fine: Fine) => {
    setActiveFine(fine);
    setDisputeReason("");
    setResolutionNote(fine.resolution_note ?? "");
  };
  const closeFine = () => {
    setActiveFine(null);
    setDisputeReason("");
    setResolutionNote("");
  };

  const handleDispute = () => {
    if (!activeFine || !disputeReason.trim()) return;
    disputeFine.mutate({ id: activeFine.id, dispute_reason: disputeReason.trim() }, { onSuccess: closeFine });
  };

  const handleResolve = (status: "upheld" | "waived") => {
    if (!activeFine) return;
    resolveFine.mutate({ id: activeFine.id, status, resolution_note: resolutionNote.trim() || undefined }, { onSuccess: closeFine });
  };

  if (error) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-sm text-destructive">Failed to load fines.</p>
      </div>
    );
  }

  const outstandingCount = fines.filter((f) => f.status === "issued" || f.status === "overdue" || f.status === "upheld").length;
  const disputedCount = fines.filter((f) => f.status === "disputed").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Fines</h1>
          <p className="text-sm text-muted-foreground">
            {outstandingCount} outstanding{disputedCount > 0 ? ` · ${disputedCount} disputed` : ""}
          </p>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setIssueOpen(true)}><Plus className="mr-1 h-4 w-4" /> Issue Fine</Button>
        )}
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : fines.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Gavel className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No fines</p>
              <p className="text-xs text-muted-foreground">
                {isAdmin ? "No fines have been issued yet." : "You have no fines on your account."}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead className="text-xs">Host</TableHead>}
                  <TableHead className="text-xs">Unit</TableHead>
                  <TableHead className="text-xs">Reason</TableHead>
                  <TableHead className="text-xs">Amount</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Due Date</TableHead>
                  <TableHead className="text-xs">Issued</TableHead>
                  <TableHead className="text-xs w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fines.map((f) => (
                  <TableRow key={f.id}>
                    {isAdmin && <TableCell className="text-xs">{f.host?.profile?.full_name || "—"}</TableCell>}
                    <TableCell className="text-sm font-medium">{f.unit?.unit_number || "—"}</TableCell>
                    <TableCell className="text-xs max-w-[240px] truncate" title={f.reason}>{f.reason}</TableCell>
                    <TableCell className="text-xs font-medium">${f.amount.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`capitalize whitespace-nowrap ${STATUS_COLORS[f.status]}`}>
                        {f.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{new Date(f.due_date).toLocaleDateString()}</TableCell>
                    <TableCell className="text-xs">{new Date(f.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button
                        variant={f.status === "issued" || f.status === "disputed" ? "outline" : "ghost"}
                        size="sm"
                        className="h-7 text-xs px-2"
                        onClick={() => openFine(f)}
                      >
                        {f.status === "issued" || f.status === "disputed" ? "Manage" : "View"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Fine Detail / Action Dialog ──────────────────────────────── */}
      <Dialog open={!!activeFine} onOpenChange={(v) => { if (!v) closeFine(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fine Record</DialogTitle>
          </DialogHeader>
          {activeFine && (
            <div className="space-y-4 py-1">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {isAdmin && (
                  <div>
                    <p className="text-xs text-muted-foreground">Host</p>
                    <p className="font-medium">{activeFine.host?.profile?.full_name || "—"}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground">Unit</p>
                  <p className="font-medium">{activeFine.unit?.unit_number || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Amount</p>
                  <p className="font-medium">${activeFine.amount.toFixed(2)} CAD</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <Badge variant="outline" className={`capitalize whitespace-nowrap ${STATUS_COLORS[activeFine.status]}`}>
                    {activeFine.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Due Date</p>
                  <p className="font-medium">{new Date(activeFine.due_date).toLocaleDateString()}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground mb-1">Reason</p>
                  <p className="text-sm bg-muted rounded-md p-2">{activeFine.reason}</p>
                </div>
              </div>

              {activeFine.dispute_reason && (
                <div className="rounded-md bg-orange-50 border border-orange-200 p-3">
                  <p className="text-xs font-medium flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Host Dispute</p>
                  <p className="text-sm mt-1">{activeFine.dispute_reason}</p>
                </div>
              )}

              {activeFine.resolution_note && (
                <div className="rounded-md bg-muted p-3">
                  <p className="text-xs font-medium">Resolution Notes</p>
                  <p className="text-sm mt-1">{activeFine.resolution_note}</p>
                </div>
              )}

              {/* Host: dispute action, only while still 'issued' */}
              {!isAdmin && activeFine.status === "issued" && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <Label className="text-xs">Dispute this fine</Label>
                    <Textarea
                      placeholder="Explain why this fine should be reconsidered..."
                      rows={3}
                      value={disputeReason}
                      onChange={(e) => setDisputeReason(e.target.value)}
                      className="text-xs"
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="w-full"
                    onClick={handleDispute}
                    disabled={disputeFine.isPending || !disputeReason.trim()}
                  >
                    Submit Dispute
                  </Button>
                </>
              )}

              {/* Host: pay action, available whenever it's outstanding and a checkout link exists */}
              {!isAdmin && (activeFine.status === "issued" || activeFine.status === "upheld" || activeFine.status === "overdue") && activeFine.stripe_checkout_url && (
                <Button size="sm" variant="outline" className="w-full" asChild>
                  <a href={activeFine.stripe_checkout_url} target="_blank" rel="noreferrer">
                    <ExternalLink className="mr-1 h-3 w-3" /> Pay Online
                  </a>
                </Button>
              )}

              {/* Admin: resolve a disputed fine */}
              {isAdmin && activeFine.status === "disputed" && (
                <>
                  <Separator />
                  <p className="text-xs font-medium text-muted-foreground">Resolve Dispute</p>
                  <div className="space-y-2">
                    <Label className="text-xs">Resolution Notes</Label>
                    <Textarea
                      placeholder="Describe the resolution decision..."
                      rows={3}
                      value={resolutionNote}
                      onChange={(e) => setResolutionNote(e.target.value)}
                      className="text-xs"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 bg-success hover:bg-success/90 text-success-foreground"
                      onClick={() => handleResolve("waived")}
                      disabled={resolveFine.isPending}
                    >
                      <CheckCircle2 className="mr-1 h-3 w-3" /> Waive Fine
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="flex-1"
                      onClick={() => handleResolve("upheld")}
                      disabled={resolveFine.isPending}
                    >
                      <XCircle className="mr-1 h-3 w-3" /> Uphold Fine
                    </Button>
                  </div>
                </>
              )}

              {/* Admin: directly issued fine — waive option without a dispute */}
              {isAdmin && activeFine.status === "issued" && (
                <>
                  <Separator />
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={() => handleResolve("waived")}
                    disabled={resolveFine.isPending}
                  >
                    Waive Fine
                  </Button>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeFine}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Issue Fine Dialog (admin) ──────────────────────────────────── */}
      <Dialog open={issueOpen} onOpenChange={(v) => { if (!v) { setIssueOpen(false); resetIssueForm(); } else setIssueOpen(true); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Issue a Fine</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Host / Unit *</Label>
              <Select value={issueForm.host_id} onValueChange={(v) => setIssueForm({ ...issueForm, host_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select host & unit" /></SelectTrigger>
                <SelectContent>
                  {hosts.map((h: any) => (
                    <SelectItem key={h.id} value={h.id}>
                      {h.profile?.full_name || "Unknown"} — Unit {h.unit?.unit_number || "?"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount" className="text-xs">Amount ($ CAD) *</Label>
                <Input
                  id="amount"
                  type="number" min="0.01" step="0.01"
                  placeholder="0.00"
                  value={issueForm.amount}
                  onChange={(e) => setIssueForm({ ...issueForm, amount: e.target.value })}
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="due_date" className="text-xs">Due Date *</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={issueForm.due_date}
                  onChange={(e) => setIssueForm({ ...issueForm, due_date: e.target.value })}
                  className="h-9"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Reason *</Label>
              <Textarea
                id="reason"
                placeholder="Describe the by-law violation or reason for this fine..."
                rows={4}
                value={issueForm.reason}
                onChange={(e) => setIssueForm({ ...issueForm, reason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIssueOpen(false); resetIssueForm(); }}>Cancel</Button>
            <Button
              onClick={handleIssue}
              disabled={issueFine.isPending || !issueForm.host_id || !issueForm.reason.trim() || !issueForm.amount || !issueForm.due_date}
            >
              {issueFine.isPending ? "Issuing..." : "Issue Fine"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
